import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  Paper,
  Chip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalesPaymentPage() {
  const pageTitle = "Payment Status";
  usePageTitle(pageTitle);

  const { ref } = useParams();
  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [copySuccess, setCopySuccess] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [charge, setCharge] = useState(null);

  // Core status check function
  const fetchPaymentStatus = useCallback(
    async (isManualTrigger = false) => {
      if (isManualTrigger) {
        setCheckingStatus(true);
      }

      await axios
        .get(`/agents/ops/sales/payment/${ref}/status.json`)
        .then(async (result) => {
          setShowLoading(false);
          setCheckingStatus(false);

          if (result.data.status === "success") {
            const chargeData = result.data.data?.result || result.data.data;
            setCharge(chargeData);
            setPageLoaded(true);

            // Handle successful payment payload
            const isSuccess =
              chargeData?.charge_status === "success" ||
              chargeData?.charge_status === "paid";

            if (isSuccess) {
              const successMessage =
                chargeData?.message ||
                "Payment received. Sales order has been processed successfully";

              await statusDialog.show(successMessage, "success");
              navigate("/ops");
            } else if (isManualTrigger) {
              setSeverity("info");
              setErrMsg(
                "Payment is still pending. Please confirm transfer completed.",
              );
              setOpen(true);
            }
          } else {
            setSeverity("error");
            setErrMsg(
              result.data.data?.message || "Failed to load payment details",
            );
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setCheckingStatus(false);
          if (isManualTrigger) {
            setSeverity("error");
            setErrMsg(
              `Unable to complete request. ${
                navigator.onLine
                  ? ""
                  : "Make sure you have an active internet connection"
              }`,
            );
            setOpen(true);
          }
        });
    },
    [ref, navigate, statusDialog],
  );

  // Initial fetch on mount & whenever manual refresh button toggles refreshPage
  useEffect(() => {
    setShowLoading(!pageLoaded);
    fetchPaymentStatus(refreshPage);
  }, [refreshPage]);

  // Auto-polling interval every 10 seconds while payment is pending
  useEffect(() => {
    let intervalId = null;

    if (charge?.charge_status === "pending") {
      intervalId = setInterval(() => {
        fetchPaymentStatus(false);
      }, 10000); // 10 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [charge?.charge_status, fetchPaymentStatus]);

  // Currency Formatter (Converts Kobo to Naira)
  const formatKoboToNaira = (amountInKobo) => {
    const nairaAmount = (Number(amountInKobo) || 0) / 100;
    return `₦${nairaAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Clipboard Handler
  const handleCopy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopySuccess(true);
    }
  };

  // Status Chip Color Resolver
  const getStatusChip = (status) => {
    const lowerStatus = String(status).toLowerCase();
    if (
      lowerStatus === "success" ||
      lowerStatus === "paid" ||
      lowerStatus === "completed"
    ) {
      return (
        <Chip
          label="PAID"
          color="success"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold" }}
        />
      );
    }
    if (lowerStatus === "failed" || lowerStatus === "expired") {
      return (
        <Chip
          label={lowerStatus.toUpperCase()}
          color="error"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold" }}
        />
      );
    }
    return (
      <Chip
        label="PENDING"
        color="warning"
        size="small"
        sx={{ color: "#fff", fontWeight: "bold" }}
      />
    );
  };

  // Extract redirect URL safely if instrument is an object with redirect_url
  const redirectUrl =
    charge?.instrument && !Array.isArray(charge.instrument)
      ? charge.instrument.redirect_url
      : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading || checkingStatus}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Main Alert Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Clipboard Confirmation Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>

      {pageLoaded && (
        <MDBox mb={5} mt={2}>
          <MDBox ml={-2} mb={1}>
            <Button
              onClick={() => window.history.go(-1)}
              startIcon={<Icon>west</Icon>}
              color="dark"
            >
              Back
            </Button>
          </MDBox>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={10} md={6} lg={5}>
              <Card sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                {/* Header & Caption */}
                <MDBox textAlign="center" mb={2}>
                  <MDBox display="flex" justifyContent="center" mb={1}>
                    {getStatusChip(charge?.charge_status)}
                  </MDBox>

                  <MDTypography variant="h5" fontWeight="bold">
                    {charge?.caption || "Payment Required"}
                  </MDTypography>

                  <MDTypography
                    variant="h3"
                    color="success"
                    fontWeight="bold"
                    mt={1}
                  >
                    {formatKoboToNaira(charge?.debit_amount || charge?.amount)}
                  </MDTypography>
                </MDBox>

                <Divider />

                {/* Dynamic Instructions */}
                {charge?.instructions && (
                  <MDTypography
                    variant="button"
                    color="text"
                    display="block"
                    textAlign="center"
                    my={2}
                  >
                    {charge.instructions}
                  </MDTypography>
                )}

                {/* TYPE 1: DISPLAY TYPE (Bank Transfer Instruments List) */}
                {Array.isArray(charge?.instrument) && (
                  <MDBox display="flex" flexDirection="column" gap={1.5} my={2}>
                    {charge.instrument.map((item, index) => {
                      const isCopyable =
                        item.key.toLowerCase().includes("account") ||
                        item.key.toLowerCase().includes("number");
                      return (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f8f9fa",
                            borderColor: "#e0e0e0",
                          }}
                        >
                          <MDBox flex={1} mr={1}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              display="block"
                              textTransform="uppercase"
                              fontWeight="bold"
                            >
                              {item.key}
                            </MDTypography>
                            <MDTypography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {item.value}
                            </MDTypography>
                          </MDBox>

                          {isCopyable && (
                            <Button
                              size="small"
                              color="info"
                              startIcon={<Icon>content_copy</Icon>}
                              onClick={() => handleCopy(item.value)}
                            >
                              Copy
                            </Button>
                          )}
                        </Paper>
                      );
                    })}
                  </MDBox>
                )}

                {/* TYPE 2: REDIRECT TYPE (External Payment Link) */}
                {redirectUrl && (
                  <MDBox my={3} textAlign="center">
                    <MDButton
                      variant="contained"
                      color="info"
                      fullWidth
                      size="large"
                      onClick={() => (window.location.href = redirectUrl)}
                      startIcon={<Icon>open_in_new</Icon>}
                    >
                      Proceed to Payment Page
                    </MDButton>
                  </MDBox>
                )}

                {/* Notes / Expiration Warnings */}
                {charge?.notes && (
                  <Alert
                    severity="warning"
                    icon={<Icon>warning</Icon>}
                    sx={{ mt: 2 }}
                  >
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                    >
                      {charge.notes}
                    </MDTypography>
                  </Alert>
                )}

                {/* Footer Controls */}
                <MDBox mt={3} display="flex" flexDirection="column" gap={3}>
                  {charge?.charge_status === "pending" && (
                    <MDButton
                      variant="contained"
                      color="success"
                      fullWidth
                      onClick={() => setRefreshPage(!refreshPage)}
                      startIcon={<Icon>sync</Icon>}
                    >
                      I Have Paid / Confirm Payment
                    </MDButton>
                  )}

                  <MDButton fullWidth onClick={() => navigate("/ops")}>
                    Close
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}
